import type { GridApi } from 'ag-grid-community';

/** Lets an app-wide event repaint every mounted *server-side* grid — registration happens in
 *  `getRows`, which client-side grids never call; those repaint from the same event's query
 *  invalidation instead. The existing refresh helpers each need the owning page to cooperate — a
 *  ref, or a flag it consumes on mount — which a caller like useAssumeRole cannot get. */
const liveGrids = new Set<GridApi>();

export function registerAgGrid(api: GridApi): void {
  if (liveGrids.has(api)) return;

  liveGrids.add(api);
  // getRows is the only hook this module has into a grid's life, so give back the other end:
  // without it the set holds every grid ever mounted until some later purge happens to notice.
  api.addEventListener('gridPreDestroyed', () => {
    liveGrids.delete(api);
  });
}

/** Test seam — grid APIs otherwise leave the set only when found destroyed during a purge. */
export function resetAgGridRegistry(): void {
  liveGrids.clear();
}

export interface AgGridPurgeResult {
  /** Grids that accepted the purge. */
  purged: number;
  /** Grids that threw and were dropped. Each may still be showing the previous role's rows, so a
   *  caller must not report an unqualified success while this is above zero. */
  failed: number;
}

/** Call *after* invalidating the relevant keys, or the re-fetch lands back in a still-fresh cache
 *  entry (staleTime is a day). Purging an uninvalidated grid is a harmless no-op repaint. */
export function purgeAllAgGridCaches(): AgGridPurgeResult {
  let purged = 0;
  let failed = 0;

  for (const api of liveGrids) {
    // Both calls are guarded, not just the refresh: this runs inside a mutation's onSuccess, which
    // query-core awaits unguarded, so an escaping throw would report an already-successful role
    // switch as failed and leave every later grid unpurged. Anything that throws here is not a
    // usable grid api, so drop it rather than let it throw again on the next switch.
    try {
      // A destroyed api warns and no-ops on every call, so drop it instead — AG Grid documents
      // isDestroyed() as the guard for exactly this "consumer holds a stale api" case.
      if (api.isDestroyed()) {
        liveGrids.delete(api);
        continue;
      }
      api.refreshServerSide({ purge: true });
      purged++;
    } catch (error) {
      liveGrids.delete(api);
      failed++;
      console.error('AG Grid purge failed for one grid; dropping it and continuing.', error);
    }
  }

  return { purged, failed };
}
